import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import { AuthModel, SimplifiedPhoenixUser } from './_models'
import * as authHelper from './AuthHelpers'
import { getUserByToken } from './_requests'
import { WithChildren } from '../../../../_metronic/helpers'
import LoadingScreen from '../../../../_metronic/layout/core/LoadingScreen'
import { clearSidebarPreferences } from '../../../../_metronic/layout/core/useSidebarState'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: SimplifiedPhoenixUser | undefined
  setCurrentUser: Dispatch<SetStateAction<SimplifiedPhoenixUser | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<SimplifiedPhoenixUser | undefined>()

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    // Clear sidebar preferences on logout so it shows expanded on next login
    clearSidebarPreferences()
  }

  return (
    <AuthContext.Provider value={{
      auth, saveAuth, currentUser, setCurrentUser, logout,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser } = useAuth()
  const didRequest = useRef(false)

  const [loading, setLoading] = useState(true)

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application

  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const user = await getUserByToken(apiToken)
          if (user) {
            setCurrentUser(user)
          }
        }
      } catch (error) {
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setLoading(false)
      }
      return () => (didRequest.current = true)
    }
    if (auth && auth.api_token) {
      requestUser(auth.api_token)
    } else {
      logout()
      setLoading(false)
    }
    // eslint-disable-next-line
  }, [])

  if (loading) {
    return <LoadingScreen />
  }

  return <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
